import { FieldValueViewProps, FieldValueView } from '@databases/fields/FieldValueView';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { useScope } from '@databases/scope';
import {
  getConvertedDatabaseValue,
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
} from '@databases/utils/database/utils';
import React, { FunctionComponent } from 'react';

export interface DatabaseValueViewProps
  extends Omit<FieldValueViewProps, 'field' | 'value'> {
  contentId: string;
  fieldId: string;
  entryId: string;
}

/**
 * Renders a database value. The right view component is automatically chosen based on the field type of the value.
 */
const DatabaseValueView: FunctionComponent<DatabaseValueViewProps> = ({
  contentId,
  fieldId,
  entryId,
  showFieldNamePlaceholder,
  ...props
}) => {
  const scope = useScope();
  const [field, value] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    const entry = getDatabaseEntryRequired(databaseDraft, entryId);
    const field = getDatabaseFieldRequired(databaseDraft, fieldId);
    const value = getConvertedDatabaseValue(databaseDraft, field, entry, state);
    return [field, value] as const;
  });

  return (
    <FieldValueView
      {...props}
      contentId={contentId}
      entryId={entryId}
      field={field}
      value={value}
      showFieldNamePlaceholder={showFieldNamePlaceholder}
    />
  );
};

export default React.memo(DatabaseValueView);
